import { useEffect, useState } from "react";
import { HandMetal, Handshake } from "lucide-react";
import { toast } from "sonner";
import { useApi } from "@/hooks/useApi";
import { getWelcome, updateWelcome, getGoodbye, updateGoodbye } from "@/api/welcome";
import { GlassCard } from "@/components/GlassCard";
import { Field, TextInput, NumInput, ColorInput, ChannelSelect, RoleSelect, ToggleRow, Textarea } from "./fields";
import { showApiError, SkeletonRows, ErrorNote } from "./helpers";
import { validateUrl } from "./EmbedBuilder";

const WelcomeCard = ({ guildId, channels }) => {
  const { data, loading, error, refetch } = useApi(() => getWelcome(guildId));
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data?.welcome && form === null) {
      setForm(data.welcome);
      setSaved(data.welcome);
    }
  }, [data, form]);

  const dirty = JSON.stringify(form) !== JSON.stringify(saved);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    for (const f of ["thumbnail_url", "image_url"]) {
      const err = validateUrl(form?.[f]);
      if (err) {
        toast.error(`${f === "thumbnail_url" ? "Thumbnail" : "Image"} URL is invalid.`);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        if (k === "guild_id") return;
        payload[k] = v === "" ? null : v;
      });
      const res = await updateWelcome(guildId, payload);
      setForm(res.welcome);
      setSaved(res.welcome);
      toast.success("Welcome settings saved.");
    } catch (e) {
      showApiError(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <GlassCard className="p-6 md:p-8" hover={false} data-testid="module-welcome">
      <div id="welcome" className="scroll-mt-40">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03]">
            <HandMetal className="h-5 w-5 text-paper-100" aria-hidden="true" />
          </span>
          <div>
            <h3 className="font-display text-lg font-semibold text-paper-100">Welcome</h3>
            <p className="text-sm text-paper-500">Greet new members — message, full embed, or welcome card.</p>
          </div>
        </div>
        {loading && <div className="mt-6"><SkeletonRows testId="welcome-loading" /></div>}
        {!loading && error && <div className="mt-6"><ErrorNote message={error.kind === "offline" || error.kind === "timeout" ? "Unable to connect to Xevon services." : error.message} onRetry={refetch} testId="welcome-error" />}</div>}
        {!loading && !error && form && (
          <div className="mt-6 space-y-4">
            <div className="grid gap-3 sm:grid-cols-3">
              <ToggleRow label="Enable welcome" checked={!!form.enabled} onChange={(v) => set({ enabled: v })} testId="welcome-enabled" />
              <ToggleRow label="Ping new member" checked={!!form.ping_member} onChange={(v) => set({ ping_member: v })} testId="welcome-ping" />
              <ToggleRow label="Use embed" checked={!!form.embed_enabled} onChange={(v) => set({ embed_enabled: v })} testId="welcome-embed-enabled" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Welcome channel">
                <ChannelSelect value={form.channel_id} onChange={(v) => set({ channel_id: v })} channels={channels} types={["text", "news"]} testId="welcome-channel" />
              </Field>
              <Field label="Delete after (seconds)">
                <NumInput value={form.delete_after} onChange={(v) => set({ delete_after: v })} testId="welcome-delete-after" />
              </Field>
            </div>
            <Field label="Message" hint="Placeholders like {user} and {server} are supported. Line breaks are preserved.">
              <Textarea rows={2} value={form.message} onChange={(v) => set({ message: v })} placeholder="Welcome {user} to {server}!" testId="welcome-message" />
            </Field>
            {!!form.embed_enabled && (
              <div className="space-y-4 rounded-xl border border-white/5 bg-white/[0.02] p-4">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-paper-500">Embed</p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Embed title">
                    <TextInput value={form.embed_title} onChange={(v) => set({ embed_title: v })} testId="welcome-embed-title" />
                  </Field>
                  <Field label="Embed color">
                    <ColorInput value={form.embed_color} onChange={(v) => set({ embed_color: v })} testId="welcome-embed-color" />
                  </Field>
                </div>
                <Field label="Embed description" hint="Line breaks are preserved in the embed.">
                  <Textarea value={form.embed_description} onChange={(v) => set({ embed_description: v })} testId="welcome-embed-description" />
                </Field>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Embed footer">
                    <TextInput value={form.embed_footer} onChange={(v) => set({ embed_footer: v })} testId="welcome-embed-footer" />
                  </Field>
                  <Field label="Thumbnail URL">
                    <TextInput value={form.thumbnail_url} onChange={(v) => set({ thumbnail_url: v })} placeholder="https://…" testId="welcome-thumbnail" />
                  </Field>
                  <Field label="Image URL">
                    <TextInput value={form.image_url} onChange={(v) => set({ image_url: v })} placeholder="https://…" testId="welcome-image" />
                  </Field>
                </div>
              </div>
            )}
            <div className="grid gap-4 sm:grid-cols-3">
              <ToggleRow label="Welcome card" checked={!!form.card_enabled} onChange={(v) => set({ card_enabled: v })} testId="welcome-card-enabled" />
              <Field label="Card type">
                <TextInput value={form.card_type} onChange={(v) => set({ card_type: v })} placeholder="default" testId="welcome-card-type" />
              </Field>
            </div>
            <div className="border-t border-white/5 pt-4">
              <button
                onClick={save}
                disabled={!dirty || saving}
                data-testid="welcome-save"
                className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-all duration-300 ${
                  dirty && !saving ? "bg-white text-black hover:scale-[1.02]" : "cursor-not-allowed border border-white/10 text-paper-500"
                }`}
              >
                {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
              </button>
            </div>
          </div>
        )}
      </div>
    </GlassCard>
  );
};

const GoodbyeCard = ({ guildId, channels, roles }) => {
  const { data, loading, error, refetch } = useApi(() => getGoodbye(guildId));
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data && form === null) {
      const g = data.goodbye || {};
      setForm(g);
      setSaved(g);
    }
  }, [data, form]);

  const dirty = JSON.stringify(form) !== JSON.stringify(saved);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    for (const f of ["thumbnail", "image", "author_icon"]) {
      const err = validateUrl(form?.[f]);
      if (err) {
        toast.error(`${f.replace("_", " ")} URL is invalid.`);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {};
      ["enabled", "channel_id", "message", "embed_color", "thumbnail", "image", "footer", "author_name", "author_icon", "mention_role"].forEach((k) => {
        if (form[k] !== undefined) payload[k] = form[k] === "" ? null : form[k];
      });
      const res = await updateGoodbye(guildId, payload);
      setForm(res.goodbye || {});
      setSaved(res.goodbye || {});
      toast.success("Goodbye settings saved.");
    } catch (e) {
      showApiError(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <GlassCard className="p-6 md:p-8" hover={false} data-testid="module-goodbye">
      <div id="goodbye" className="scroll-mt-40">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03]">
            <Handshake className="h-5 w-5 text-paper-100" aria-hidden="true" />
          </span>
          <div>
            <h3 className="font-display text-lg font-semibold text-paper-100">Goodbye</h3>
            <p className="text-sm text-paper-500">Send a farewell when members leave.</p>
          </div>
        </div>
        {loading && <div className="mt-6"><SkeletonRows testId="goodbye-loading" /></div>}
        {!loading && error && <div className="mt-6"><ErrorNote message={error.kind === "offline" || error.kind === "timeout" ? "Unable to connect to Xevon services." : error.message} onRetry={refetch} testId="goodbye-error" />}</div>}
        {!loading && !error && form && (
          <div className="mt-6 space-y-4">
            <ToggleRow label="Enable goodbye" checked={!!form.enabled} onChange={(v) => set({ enabled: v })} testId="goodbye-enabled" />
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Goodbye channel">
                <ChannelSelect value={form.channel_id} onChange={(v) => set({ channel_id: v })} channels={channels} types={["text", "news"]} testId="goodbye-channel" />
              </Field>
              <Field label="Mention role">
                <RoleSelect value={form.mention_role} onChange={(v) => set({ mention_role: v })} roles={roles.filter((r) => !r.managed && r.name !== "@everyone")} testId="goodbye-mention-role" />
              </Field>
            </div>
            <Field label="Message" hint="Line breaks are preserved.">
              <Textarea rows={2} value={form.message} onChange={(v) => set({ message: v })} placeholder="Goodbye {user}…" testId="goodbye-message" />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Accent color">
                <ColorInput value={form.embed_color} onChange={(v) => set({ embed_color: v })} testId="goodbye-color" />
              </Field>
              <Field label="Author name">
                <TextInput value={form.author_name} onChange={(v) => set({ author_name: v })} testId="goodbye-author" />
              </Field>
              <Field label="Thumbnail URL">
                <TextInput value={form.thumbnail} onChange={(v) => set({ thumbnail: v })} placeholder="https://…" testId="goodbye-thumbnail" />
              </Field>
              <Field label="Image URL">
                <TextInput value={form.image} onChange={(v) => set({ image: v })} placeholder="https://…" testId="goodbye-image" />
              </Field>
              <Field label="Footer text">
                <TextInput value={form.footer} onChange={(v) => set({ footer: v })} testId="goodbye-footer" />
              </Field>
            </div>
            <div className="border-t border-white/5 pt-4">
              <button
                onClick={save}
                disabled={!dirty || saving}
                data-testid="goodbye-save"
                className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-all duration-300 ${
                  dirty && !saving ? "bg-white text-black hover:scale-[1.02]" : "cursor-not-allowed border border-white/10 text-paper-500"
                }`}
              >
                {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
              </button>
            </div>
          </div>
        )}
      </div>
    </GlassCard>
  );
};

export const WelcomeGoodbye = { WelcomeCard, GoodbyeCard };
